import React, { useState, useEffect } from 'react';
import { X, MoreVertical, Plus } from 'lucide-react';
import { ProjectCard, Task, ProjectColumn } from '../../types/projects';
import { projectsService } from '../../services/projects';
import { useAuth } from '../../contexts/AuthContext';
import { sanitizeHtml } from '../../utils/sanitize';

interface CardDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: ProjectCard;
  onEdit: () => void;
  readOnly?: boolean;
  setColumns: React.Dispatch<React.SetStateAction<ProjectColumn[]>>;
}

interface TaskMenuProps {
  taskId: string;
  onEdit: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

const TaskMenu: React.FC<TaskMenuProps> = ({ taskId, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(taskId);
    setIsOpen(false);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(taskId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-700 rounded-lg shadow-lg z-10">
          <div className="py-1">
            <button
              onClick={handleEdit}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              Modifier
            </button>
            <button
              onClick={handleDelete}
              className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const CardDetailModal: React.FC<CardDetailModalProps> = ({
  isOpen,
  onClose,
  card,
  onEdit,
  readOnly = false,
  setColumns
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTask, setNewTask] = useState({
    description: '',
    responsible: ''
  });
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (card.tasks) {
      setTasks([...card.tasks]);
    }
  }, [card.tasks]);

  const updateCardTasks = (updatedTasks: Task[]) => {
    setColumns(prevColumns => {
      const newColumns = prevColumns.map(col => ({
        ...col,
        cards: col.cards.map(c => 
          c.id === card.id
            ? { ...c, tasks: [...updatedTasks] }
            : c
        )
      }));

      projectsService.saveAll(newColumns).catch(console.error);
      return newColumns;
    });
  };

  const handleAddTask = () => {
    if (!isSuperAdmin || !newTask.description || !newTask.responsible) return;

    const task: Task = {
      id: `task-${Date.now()}`,
      description: newTask.description,
      responsible: newTask.responsible,
      completed: false
    };

    const updatedTasks = [...tasks, task];
    setTasks(updatedTasks);
    updateCardTasks(updatedTasks);
    setNewTask({ description: '', responsible: '' });
  };

  const handleEditTask = (taskId: string) => {
    if (!isSuperAdmin) return;
    
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setEditingTask(task);
      setNewTask({
        description: task.description,
        responsible: task.responsible
      });
    }
  };

  const handleUpdateTask = () => {
    if (!isSuperAdmin || !editingTask || !newTask.description || !newTask.responsible) return;

    const updatedTasks = tasks.map(task =>
      task.id === editingTask.id
        ? {
            ...task,
            description: newTask.description,
            responsible: newTask.responsible
          }
        : task
    );

    setTasks(updatedTasks);
    updateCardTasks(updatedTasks);
    setEditingTask(null);
    setNewTask({ description: '', responsible: '' });
  };

  const handleDeleteTask = (taskId: string) => {
    if (!isSuperAdmin) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) {
      const updatedTasks = tasks.filter(task => task.id !== taskId);
      setTasks(updatedTasks);
      updateCardTasks(updatedTasks);
    }
  };

  const handleToggleTask = (taskId: string) => {
    if (!isSuperAdmin) return;

    const updatedTasks = tasks.map(task =>
      task.id === taskId
        ? { ...task, completed: !task.completed }
        : task
    );
    setTasks(updatedTasks);
    updateCardTasks(updatedTasks);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl p-6 m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {card.title}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {card.label && (
          <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium mb-4 ${
            card.label === 'warning'
              ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20'
              : 'bg-red-100 text-red-600 dark:bg-red-900/20'
          }`}>
            {card.label === 'warning' ? 'Attention' : 'Urgent'}
          </div>
        )}

        <div
          className="prose prose-sm dark:prose-invert max-w-none mt-4 mb-6"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.content) }}
        />

        <div className="space-y-6">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Tâches</h3>
              {isSuperAdmin && (
                <button
                  onClick={() => setEditingTask(null)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une tâche
                </button>
              )}
            </div>

            {isSuperAdmin && (!editingTask || editingTask === null) && (
              <div className="mb-4 grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Description de la tâche"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2"
                />
                <input
                  type="text"
                  placeholder="Responsable"
                  value={newTask.responsible}
                  onChange={(e) => setNewTask({ ...newTask, responsible: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2"
                />
                <button
                  onClick={editingTask ? handleUpdateTask : handleAddTask}
                  className="col-span-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingTask ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            )}

            <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden mb-8">
              <div className="max-h-[400px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-8">
                        Fait
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tâche
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Responsable
                      </th>
                      {isSuperAdmin && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {tasks.map((task) => (
                      <tr key={task.id}>
                        <td className="px-6 py-4">
                          {isSuperAdmin ? (
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => handleToggleTask(task.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                            />
                          ) : (
                            <div className="h-4 w-4 border border-gray-300 rounded flex items-center justify-center">
                              {task.completed && (
                                <div className="h-2 w-2 bg-blue-600 rounded-sm" />
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={task.completed ? 'line-through text-gray-400' : ''}>
                            {task.description}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {task.responsible}
                        </td>
                        {isSuperAdmin && (
                          <td className="px-6 py-4">
                            <TaskMenu
                              taskId={task.id}
                              onEdit={handleEditTask}
                              onDelete={handleDeleteTask}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Fermer
              </button>
              <button
                onClick={onEdit}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Modifier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};